import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useReducer, useRef } from 'react';
import { getNewUTCDate } from "../../../utils/time";
import { FEATURES } from "../../../lib/services/copilot";
import { getAgents } from "../../../lib/services/copilotAgents";
import { useWeb5Context } from "../../../lib/Web5Context";
import { errorToast } from "../../../utils/toast";

/**
 * Default properties for a chat interaction
 */
const defaultChat = {
  content: '',
  // Using content instead of text for consistency
  role: '',
  vote: '',
  isLoading: false,
  isFinished: false,
  isError: false,
  parentId: null,
  actions: []
};

/**
 * Initial state for the copilot reducer
 * @property {Object} chat - Map of chat interactions
 * @property {Object} chatSession - Current chat session data
 * @property {boolean} hasInitialized - Whether the copilot has been initialized
 * @property {Array} copilotAgents - List of available copilot agents
 * @property {Object|null} activeAgent - Currently selected copilot agent
 */
const initialState = {
  chat: {},
  chatSession: {},
  hasInitialized: false,
  copilotAgents: [],
  activeAgent: null
};

/**
 * Action types for the copilot reducer
 * @property {string} SET_INTERACTION - Update or create a chat interaction
 * @property {string} CLEAR_CHAT - Clear all chat interactions
 * @property {string} SET_CHAT_SESSION - Set the current chat session
 * @property {string} SET_INITIALIZED - Set initialization status
 * @property {string} SET_COPILOT_AGENTS - Set available copilot agents
 * @property {string} SET_ACTIVE_AGENT - Set the active copilot agent
 */
const ACTIONS = {
  SET_INTERACTION: 'SET_INTERACTION',
  CLEAR_CHAT: 'CLEAR_CHAT',
  SET_CHAT_SESSION: 'SET_CHAT_SESSION',
  SET_INITIALIZED: 'SET_INITIALIZED',
  SET_COPILOT_AGENTS: 'SET_COPILOT_AGENTS',
  SET_ACTIVE_AGENT: 'SET_ACTIVE_AGENT'
};

/**
 * Reducer function for managing copilot state
 */
const copilotReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_INTERACTION:
      {
        const {
          id,
          text,
          responseId,
          isFinished,
          isQuestion,
          isLoading,
          isError,
          parentId,
          action: actionProp
        } = action.payload;
        if (!id) return state;
        const interaction = state.chat[id] || _objectSpread({
          creationDate: getNewUTCDate()
        }, defaultChat);
        const updatedInteraction = _objectSpread({}, interaction);
        if (isError) {
          updatedInteraction.isError = true;
          updatedInteraction.isFinished = true;
          updatedInteraction.isLoading = false;
        } else {
          if (text) updatedInteraction.content = text;
          if (typeof isLoading === 'boolean') updatedInteraction.isLoading = isLoading;
          if (isFinished) updatedInteraction.isFinished = isFinished;
          if (responseId) updatedInteraction.responseId = responseId;
          if (parentId) updatedInteraction.parentId = parentId;
          if (actionProp) updatedInteraction.actions = [...updatedInteraction.actions, actionProp];
        }
        updatedInteraction.role = isQuestion ? 'user' : 'assistant';
        return _objectSpread(_objectSpread({}, state), {}, {
          chat: _objectSpread(_objectSpread({}, state.chat), {}, {
            [id]: updatedInteraction
          })
        });
      }
    case ACTIONS.CLEAR_CHAT:
      return _objectSpread(_objectSpread({}, state), {}, {
        chat: {},
        chatSession: {},
        hasInitialized: false
      });
    case ACTIONS.SET_CHAT_SESSION:
      return _objectSpread(_objectSpread({}, state), {}, {
        chatSession: action.payload
      });
    case ACTIONS.SET_INITIALIZED:
      return _objectSpread(_objectSpread({}, state), {}, {
        hasInitialized: action.payload
      });
    case ACTIONS.SET_COPILOT_AGENTS:
      return _objectSpread(_objectSpread({}, state), {}, {
        copilotAgents: action.payload
      });
    case ACTIONS.SET_ACTIVE_AGENT:
      return _objectSpread(_objectSpread({}, state), {}, {
        activeAgent: action.payload
      });
    default:
      return state;
  }
};

/**
 * Custom hook for managing copilot chat interactions, agent selection, and streaming operations
 *
 * This hook provides a comprehensive interface for managing copilot functionality including:
 * - Chat interaction management (create, update, clear)
 * - Agent selection and management
 * - Chat session handling
 * - Stream reader management for real-time responses
 * - Initialization state tracking
 *
 * @returns {Object} Chat management functions and state
 * @property {Object} chat - Map of chat interactions indexed by interaction ID
 * @property {Object} chatSession - Current chat session data containing session metadata
 * @property {boolean} hasInitialized - Whether the copilot has been initialized and is ready for use
 * @property {Array} copilotAgents - List of available copilot agents with their configurations
 * @property {Object|null} activeAgent - Currently selected copilot agent object, or null if none selected
 * @property {Function} setInteraction - Update or create a chat interaction with specified parameters
 * @property {Function} clearChat - Clear all chat interactions and reset the chat state
 * @property {Function} setChatSession - Set the current chat session with new session data
 * @property {Function} setHasInitialized - Set initialization status (true when ready, false when not)
 * @property {Function} setActiveAgent - Set the active copilot agent (agent object or null to clear)
 * @property {Function} setCopilotAgents - Set the complete list of available copilot agents
 * @property {Function} refreshAgents - Fetch and refresh the agents list from the API, updating state
 * @property {Function} getStreamReader - Get the current active stream reader for ongoing operations
 * @property {Function} setStreamReader - Set the active stream reader for managing streaming responses
 */
const useCopilot = () => {
  const {
    t
  } = useWeb5Context();
  const [state, dispatch] = useReducer(copilotReducer, initialState);
  const streamReaderRef = useRef(null); // Track active stream reader

  /**
   * Fetches and updates the agents list
   * Keeps the current activeAgent if it exists in the new list, otherwise sets the default agent
   * @returns {Promise<Array>} Promise that resolves to the agents data array
   * @throws {Error} When the API call fails or encounters an error
   */
  const handleGetAgents = useCallback(() => {
    return;
    // TODO: Uncomment this when the API is ready
    // return getAgents()
    //     .then(({ data }) => {
    //         dispatch({ type: ACTIONS.SET_COPILOT_AGENTS, payload: data });

    //         let newActiveAgent;

    //         if (
    //             !state.activeAgent ||
    //             (state.activeAgent &&
    //                 !data.find((agent) => agent.name === state?.activeAgent?.name))
    //         ) {
    //             newActiveAgent = data.find((agent) => agent.name === FEATURES.SAGE_COPILOT);
    //             newActiveAgent &&
    //                 dispatch({ type: ACTIONS.SET_ACTIVE_AGENT, payload: newActiveAgent });
    //         }

    //         return data;
    //     })
    //     .catch((error) => {
    //         console.error(error);
    //         errorToast({
    //             t,
    //             title: t('cfm_label_error_generic'),
    //         });
    //         throw error;
    //     });
  }, []);

  /**
   * Updates or creates a chat interaction
   * @param {Object} params - Interaction parameters
   * @param {string} [params.text] - Chat message content
   * @param {string} params.id - Unique identifier for the interaction (required)
   * @param {string} [params.responseId] - ID of the response
   * @param {boolean} [params.isFinished] - Whether the interaction is complete
   * @param {boolean} [params.isQuestion] - Whether this is a user question (determines role)
   * @param {boolean} [params.isLoading] - Whether the interaction is loading
   * @param {boolean} [params.isError] - Whether an error occurred
   * @param {string} [params.parentId] - ID of the parent interaction
   * @param {Object} [params.action] - Action object to be added to the interaction's actions array
   */
  const setInteraction = useCallback(_ref => {
    let {
      text,
      id,
      responseId,
      isFinished,
      isQuestion,
      isLoading,
      isError,
      parentId,
      action
    } = _ref;
    dispatch({
      type: ACTIONS.SET_INTERACTION,
      payload: {
        text,
        id,
        responseId,
        isFinished,
        isQuestion,
        isLoading,
        isError,
        parentId,
        action
      }
    });
  }, []);

  /**
   * Clears all chat interactions and resets the chat state
   */
  const clearChat = useCallback(() => {
    dispatch({
      type: ACTIONS.CLEAR_CHAT
    });
  }, []);

  /**
   * Sets the currently active copilot agent
   * @param {Object|null} payload - The agent object to set as active, or null to clear
   */
  const setActiveAgent = useCallback(payload => {
    dispatch({
      type: ACTIONS.SET_ACTIVE_AGENT,
      payload
    });
  }, []);

  /**
   * Updates the list of available copilot agents
   * @param {Array} payload - Array of agent objects
   */
  const setCopilotAgents = useCallback(payload => {
    dispatch({
      type: ACTIONS.SET_COPILOT_AGENTS,
      payload
    });
  }, []);

  /**
   * Sets the active stream reader for managing streaming responses
   * @param {ReadableStreamDefaultReader|null} reader - The stream reader to set as active
   */
  const setStreamReader = useCallback(reader => {
    streamReaderRef.current = reader;
  }, []);

  /**
   * Gets the current active stream reader
   * @returns {ReadableStreamDefaultReader|null} The current active stream reader, or null if none is set
   */
  const getStreamReader = useCallback(() => streamReaderRef === null || streamReaderRef === void 0 ? void 0 : streamReaderRef.current, []);
  return {
    activeAgent: state.activeAgent,
    copilotAgents: state.copilotAgents,
    setActiveAgent,
    setCopilotAgents,
    refreshAgents: handleGetAgents,
    setInteraction,
    clearChat,
    chat: state.chat,
    chatSession: state.chatSession,
    setChatSession: session => dispatch({
      type: ACTIONS.SET_CHAT_SESSION,
      payload: session
    }),
    hasInitialized: state.hasInitialized,
    setHasInitialized: value => dispatch({
      type: ACTIONS.SET_INITIALIZED,
      payload: value
    }),
    getStreamReader,
    setStreamReader
  };
};
export default useCopilot;